import { useCallback, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { PolicyKey } from '@kirin/api-client/contracts/policies';
import {
  PACKAGE_RULE_ECOSYSTEMS,
  PolicyExceptionAction,
  packageIdentityName,
  type PolicyException,
} from '@kirin/api-client/contracts/policy-exceptions';
import type { OrganizationId } from '@kirin/api-client/contracts/organizations';
import {
  listOrganizationPolicyExceptions,
  listUserPolicyExceptions,
} from '@kirin/api-client/api/policy-exceptions';
import { ApiError } from '@kirin/api-client/errors';
import {
  useCreateOrganizationPolicyException,
  useDeleteOrganizationPolicyException,
  useOrganizationPolicyExceptions,
} from './useOrganizationPolicyExceptions';
import {
  useCreateUserPolicyException,
  useDeleteUserPolicyException,
  useUserPolicyExceptions,
} from './useUserPolicyExceptions';
import { queryKeys } from './queryKeys';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api-client';
import { getApiErrorMessage } from '@/lib/api-errors';
import { trackEvent } from '@/lib/analytics';

/** The two fields that identify a package rule. */
export interface PackageIdentity {
  name: string;
  ecosystem: string;
}

/** Where a package Block / Unblock was made, for analytics. */
export type PackageListSource = 'alert' | 'packages_page';

/**
 * Packages have no Allow list: the backend refuses package Allow rules, so
 * the only states are Blocked and not blocked.
 *
 * Every action resolves, never rejects: a failure is reported with a toast
 * inside the hook, so a caller's `void actions.block(...)` cannot produce an
 * unhandled rejection.
 */
export interface PackageListActions {
  /** Put the package on the Block list. */
  block: (pkg: PackageIdentity) => Promise<void>;
  /** Remove the package's Block rule, returning it to plain detection. */
  clear: (pkg: PackageIdentity) => Promise<void>;
  /**
   * True for this package from the click until the Packages table has
   * refetched — not merely until the mutation settles. In between, the row
   * still shows the old state and the rule lists may not include the new rule
   * yet, so a second click would act on stale data and send a duplicate
   * create. Other rows stay usable meanwhile.
   */
  isPending: (pkg: PackageIdentity) => boolean;
  /** Who a rule written here applies to — drives the Block confirmation copy. */
  scope: 'organization' | 'account';
}

/** The OSV casing of an ecosystem (`pypi` → `PyPI`), or the trimmed input when it names none we know. */
export function canonicalEcosystem(value: string): string {
  const trimmed = value.trim();
  const lower = trimmed.toLowerCase();
  return (
    PACKAGE_RULE_ECOSYSTEMS.find(
      ecosystem => ecosystem.toLowerCase() === lower
    ) ?? trimmed
  );
}

/** Identity key for the in-flight set, using the same comparison as {@link rulesFor}. */
export function packageKey(pkg: PackageIdentity): string {
  const ecosystem = canonicalEcosystem(pkg.ecosystem);
  return `${ecosystem}\u0000${packageIdentityName(pkg.name, ecosystem)}`;
}

/**
 * Registry identity on name, case-insensitive on ecosystem, blank fields or
 * any key besides `name` / `ecosystem` matching nothing — the same comparison
 * the backend matcher (`matchesRuleForPackages`) and the list-state keys
 * (`toPackageRuleKeys`) make. So a PyPI `Evil_Pkg` row resolves the `evil-pkg`
 * rule it is listed under, and a rule stored as `pypi` is found for a `PyPI`
 * row.
 */
function rulesFor(
  exceptions: PolicyException[] | undefined,
  pkg: PackageIdentity
): PolicyException[] {
  const ecosystem = canonicalEcosystem(pkg.ecosystem);
  const name = packageIdentityName(pkg.name, ecosystem);
  return (exceptions ?? []).filter(exception => {
    const ruleName = exception.ruleObject.name;
    const ruleEcosystem = exception.ruleObject.ecosystem;
    return (
      Object.keys(exception.ruleObject).every(
        key => key === 'name' || key === 'ecosystem'
      ) &&
      typeof ruleName === 'string' &&
      typeof ruleEcosystem === 'string' &&
      ruleName.trim() !== '' &&
      canonicalEcosystem(ruleEcosystem) === ecosystem &&
      packageIdentityName(ruleName, ecosystem) === name
    );
  });
}

/**
 * Whether a package is Blocked — on the Block list, the same answer as the
 * Packages page's `listState`.
 *
 * For surfaces with no Packages row to read `listState` from (an alert).
 * Reads the cached rule lists, which the create/delete mutations invalidate,
 * so the answer follows this user's own Block / Unblock.
 *
 * @returns `blocked` is undefined until the Block list has loaded; `isError`
 * is true once it has failed to load, so a caller can say so instead of
 * waiting forever.
 */
export function usePackageBlocked(
  organizationId: OrganizationId | undefined,
  pkg: PackageIdentity | null
): { blocked: boolean | undefined; isError: boolean } {
  const policyKey = PolicyKey.PackagesManagement;
  const isOrg = organizationId != null;
  const enabled = pkg != null;
  const orgBlock = useOrganizationPolicyExceptions(
    organizationId,
    policyKey,
    PolicyExceptionAction.Block,
    enabled && isOrg
  );
  const userBlock = useUserPolicyExceptions(
    policyKey,
    PolicyExceptionAction.Block,
    enabled && !isOrg
  );
  const blockList = isOrg ? orgBlock : userBlock;
  const isError = blockList.isError === true;
  const blockRules = blockList.data;
  if (pkg == null || blockRules == null) {
    return { blocked: undefined, isError };
  }
  return { blocked: rulesFor(blockRules, pkg).length > 0, isError };
}

/**
 * Block / clear actions for a package, from a Packages row or an alert
 * (KIR-4443, AC 9), for either the org or the personal scope.
 *
 * The Block list is fetched fresh inside each action, and used ONLY to
 * resolve the exception ids it needs — never to render a row's state. A
 * cached copy can lag another admin's change or this user's last click. State
 * comes from the server-computed `listState` on the row, because that is what
 * the AC 10 filter and pagination are built from, and two independently
 * derived answers on the same screen would eventually disagree in front of a
 * user.
 *
 * A successful change emits `package_list_change` with the given `source`.
 */
export function usePackageListActions(
  organizationId: OrganizationId | undefined,
  source: PackageListSource
): PackageListActions {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const policyKey = PolicyKey.PackagesManagement;
  // The ref is the real guard: it updates synchronously, so two clicks on the
  // same row in the same frame cannot both pass. The state mirrors it for
  // rendering.
  const inFlight = useRef(new Set<string>());
  const [busy, setBusy] = useState<ReadonlySet<string>>(new Set());
  const isOrg = organizationId != null;

  const fetchBlockRules = useCallback((): Promise<PolicyException[]> => {
    const action = PolicyExceptionAction.Block;
    return organizationId != null
      ? queryClient.fetchQuery({
          queryKey: queryKeys.policyExceptions.organization(
            organizationId,
            policyKey,
            action
          ),
          queryFn: async () =>
            (
              await listOrganizationPolicyExceptions(
                apiClient,
                organizationId,
                policyKey,
                action
              )
            ).exceptions,
          staleTime: 0,
        })
      : queryClient.fetchQuery({
          queryKey: queryKeys.policyExceptions.user(policyKey, action),
          queryFn: async () =>
            (await listUserPolicyExceptions(apiClient, policyKey, action))
              .exceptions,
          staleTime: 0,
        });
  }, [queryClient, organizationId, policyKey]);

  const orgCreate = useCreateOrganizationPolicyException(
    organizationId,
    policyKey
  );
  const orgDelete = useDeleteOrganizationPolicyException(
    organizationId,
    policyKey
  );
  const userCreate = useCreateUserPolicyException(policyKey);
  const userDelete = useDeleteUserPolicyException(policyKey);

  // `useMutation` returns a new object every render; only `mutateAsync` is
  // stable. Depending on the whole object would rebuild every action, and so
  // the Packages columns, on each render — and the DataTable re-derives its
  // URL state from its columns, which loops.
  const createRule = (isOrg ? orgCreate : userCreate).mutateAsync;
  const removeRule = (isOrg ? orgDelete : userDelete).mutateAsync;

  /**
   * Refetch the Packages table and resolve once it has landed. The mutation
   * hooks invalidate it too, but without awaiting; this one is awaited so
   * {@link PackageListActions.isPending} covers the refetch window.
   */
  const refreshPackages = useCallback(
    (): Promise<void> =>
      queryClient.invalidateQueries({
        queryKey: [
          'agent-command-events',
          organizationId ?? 'account',
          'aggregated-packages',
        ],
      }),
    [queryClient, organizationId]
  );

  const run = useCallback(
    async (
      pkg: PackageIdentity,
      change: 'block' | 'unblock',
      failureTitle: string,
      work: () => Promise<void>
    ): Promise<void> => {
      const key = packageKey(pkg);
      if (inFlight.current.has(key)) return;
      inFlight.current.add(key);
      setBusy(new Set(inFlight.current));
      try {
        await work();
        trackEvent('package_list_change', {
          scope: isOrg ? 'organization' : 'account',
          source,
          change,
          ecosystem: canonicalEcosystem(pkg.ecosystem),
        });
      } catch (error) {
        toast({
          title: failureTitle,
          description: getApiErrorMessage(error, 'Please try again.'),
          variant: 'destructive',
        });
      } finally {
        // Refetch on failure too: another admin may have changed the row.
        await refreshPackages().catch(() => undefined);
        inFlight.current.delete(key);
        setBusy(new Set(inFlight.current));
      }
    },
    [toast, refreshPackages, isOrg, source]
  );

  const block = useCallback(
    (pkg: PackageIdentity) =>
      run(pkg, 'block', `Could not block ${pkg.name}`, async () => {
        // Already blocked: nothing to create. The API would answer 409 rather
        // than be a no-op, which would surface as a failed action for
        // something the admin's intent already holds true for.
        if (rulesFor(await fetchBlockRules(), pkg).length > 0) return;
        try {
          await createRule({
            action: PolicyExceptionAction.Block,
            ruleObject: { name: pkg.name, ecosystem: pkg.ecosystem },
          });
        } catch (error) {
          // A 409 means someone else got there first: the Block holds.
          if (!(error instanceof ApiError && error.status === 409)) throw error;
        }
      }),
    [run, fetchBlockRules, createRule]
  );
  const clear = useCallback(
    (pkg: PackageIdentity) =>
      run(pkg, 'unblock', `Could not unblock ${pkg.name}`, async () => {
        for (const exception of rulesFor(await fetchBlockRules(), pkg)) {
          await removeRule(exception.id);
        }
      }),
    [run, fetchBlockRules, removeRule]
  );

  const isPending = useCallback(
    (pkg: PackageIdentity) => busy.has(packageKey(pkg)),
    [busy]
  );
  return useMemo<PackageListActions>(
    () => ({
      block,
      clear,
      isPending,
      scope: isOrg ? 'organization' : 'account',
    }),
    [block, clear, isPending, isOrg]
  );
}
